import UserProxy from "../../utility/proxy/manager/userProxy";

/****************************************************
 * Module Name:  UserDetailModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState, useEffect } from "react";
import { Box, Button, Container, Tooltip } from "@mui/material";
import Constants from "../../utility/constants";
import Modal from "react-bootstrap/Modal";
import logo from '../../images/pansuriya.jpg'
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CommonSnackbar from "../common/commonSnackBar";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";

const UserDetailModal = ({
    userids, selectedIds, usertype, isMulti, handleClose, handleSelect, show,
}) => {

    const [data, setData] = useState([]);
    const [filterValues, setFilterValues] = useState({});
    const [selectionModel, setSelectionModel] = useState(selectedIds);
    const [detailLoader, setDetailLoader] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);

    useEffect(() => {
        handleSearch(userids);
    }, [])

    useEffect(() => {
        if (selectedIds && selectedIds.length > 0) {
            setSelectionModel(selectedIds);
        }
    }, [selectedIds]);

    const handleSearch = (userids) => {
        setDetailLoader(true);
        UserProxy.GetUserDetail(
            userids,
            (responseData) => {
                if (responseData) {
                    const uniqueFilterValues = {
                        companyname: Utility.getDistinctOptions(responseData, "companyname"),
                    };
                    setFilterValues(uniqueFilterValues);
                    setData(responseData);
                    setDetailLoader(false)
                } else {
                    setErrorSnackbar(true)
                    setDetailLoader(false)
                }
            }
        );
    }

    const handleOnSelect = (rowId) => {
        const selectedIds = rowId ? [rowId] : isMulti ? selectionModel : [selectionModel[0]];
        handleSelect(selectedIds);
    }

    const handleRowSelection = (params) => {
        return isMulti || selectionModel.length === 0 || selectionModel.includes(params.id);
    };


    const initialColumns = () => [
        {
            field: "id",
            headerName: "ID",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "companyname",
            headerName: "COMPANY NAME",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.companyname : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "firstname",
            headerName: "FIRST NAME",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "lastname",
            headerName: "LAST NAME",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "phone",
            headerName: "PHONE",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "email",
            headerName: "EMAIL",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "username",
            headerName: "USERNAME",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
    ];

    const columnNames = initialColumns();

    return (
        <Box>
            <Modal container={document.body} show={show} onHide={handleClose} dialogClassName="detail-modal" enforceFocus={false} style={{ zIndex: 1300 }}>
                <CommonSnackbar
                    show={errorSnackbar}
                    message={Constants.APiFailErrorMessage}
                    variant={"error"}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={"Failed"}
                    handleClose={() => setErrorSnackbar(false)}
                />
                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;
                        {usertype === 'C' ? 'Customer Detail' :
                            usertype === 'E' ? 'Employee Detail' :
                                usertype === 'B' ? 'Broker Detail' :
                                    'User Detail'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Container maxWidth="xxl">
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                                    color: "#1976d2",
                                    padding: "4px 5px",
                                    borderRadius: "4px",
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                                    color: "#1976d2",
                                    padding: "5px 5px",
                                    borderRadius: "4px",
                                },
                                "& .blueCell": {
                                    color: "#080808",
                                    backgroundColor: "#d4eff8",
                                },
                                height: "50vh",
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={true}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "8px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "15px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "22px",
                                    },
                                    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                                        width: "0.4em",
                                        height: "0.7em",
                                    },
                                    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
                                        background: "#f1f1f1",
                                    },
                                    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                                        backgroundColor: "#888",
                                    },
                                    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
                                        background: "#555",
                                    },

                                    '& .MuiDataGrid-menu, & .MuiPopper-root': {
                                        zIndex: 1400,
                                    },
                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                density="compact"
                                slots={{ toolbar: GridToolbar }}
                                rows={data}
                                columns={columnNames}
                                loading={detailLoader}
                                getRowId={(row) => row.id}
                                checkboxSelection={isMulti}
                                isRowSelectable={handleRowSelection}
                                onRowSelectionModelChange={(newSelection) => {
                                    setSelectionModel(isMulti ? newSelection : newSelection.slice(-1));
                                }}
                                onRowClick={(params) => {
                                    if (!isMulti) {
                                        setSelectionModel([params.id]);
                                    }
                                }}
                                rowSelectionModel={selectionModel}
                                onRowDoubleClick={(params) => {
                                    if (!isMulti) {
                                        const clickedId = params.id;
                                        const isSelectable = selectionModel.length === 0 || selectionModel.includes(clickedId);
                                        if (isSelectable) {
                                            setSelectionModel([clickedId]);
                                            handleOnSelect(clickedId);
                                        }
                                    }
                                }}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns id, the other columns will remain visible
                                            id: false,
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                            <Box style={{ position: "absolute", top: 7, right: "257px" }}>
                                {detailLoader ? (
                                    <CircularProgress size="1rem" />
                                ) : (
                                    <Tooltip title="Reload List">
                                        <RefreshOutlinedIcon
                                            onClick={() => handleSearch(userids)}
                                            color="primary"
                                            fontSize="medium"
                                            sx={{ cursor: "pointer", mr: 1 }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>

                    </Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="success" onClick={() => handleOnSelect()} disabled={selectionModel.length === 0}>
                        Select
                    </Button>
                    <Button variant="contained" color="error" onClick={handleClose} style={{ marginLeft: "10px" }}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Box>
    );
}

export default UserDetailModal;
